"use client";

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useTranslations } from "next-intl";
import type { CountryCode } from "libphonenumber-js/min";
import { filterCountries, flagUrl, type CountryOption } from "@/lib/countries";

interface BaseProps {
  value: CountryCode;
  onChange: (code: CountryCode) => void;
  options: readonly CountryOption[];
  placeholder?: string;
  noResultsLabel?: string;
}

function FlagImg({ code, size = 24 }: { code: string; size?: number }) {
  // Width 40 source for crisp 2× display, then constrained via CSS so the
  // file weight stays small but high-DPI screens get a sharp image.
  return (
    <span
      className="inline-block shrink-0 overflow-hidden bg-border/40 rounded-[2px]"
      style={{ width: size, height: Math.round(size * 0.75) }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={flagUrl(code, 40)}
        alt=""
        width={size}
        height={Math.round(size * 0.75)}
        loading="lazy"
        className="block w-full h-full object-cover"
      />
    </span>
  );
}

interface ListProps {
  options: readonly CountryOption[];
  activeIdx: number;
  onSelect: (code: CountryCode) => void;
  onHover: (idx: number) => void;
  showDialCode?: boolean;
  listId: string;
  noResultsLabel: string;
}

function CountryList({
  options,
  activeIdx,
  onSelect,
  onHover,
  showDialCode,
  listId,
  noResultsLabel,
}: ListProps) {
  if (options.length === 0) {
    return (
      <ul role="listbox" id={listId} className="max-h-[280px] overflow-y-auto py-2">
        <li className="px-4 py-3 text-[14px] text-text-tertiary">{noResultsLabel}</li>
      </ul>
    );
  }
  return (
    <ul role="listbox" id={listId} className="max-h-[280px] overflow-y-auto py-1">
      {options.map((opt, idx) => {
        const active = idx === activeIdx;
        return (
          <li
            key={opt.code}
            id={`${listId}-opt-${opt.code}`}
            role="option"
            aria-selected={active}
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(opt.code);
            }}
            onMouseEnter={() => onHover(idx)}
            className={`flex items-center gap-3 px-4 py-2 cursor-pointer text-[15px] ${
              active ? "bg-surface" : "bg-background hover:bg-surface"
            }`}
          >
            <FlagImg code={opt.code} size={24} />
            <span className="flex-1 text-foreground truncate">{opt.name}</span>
            {showDialCode && (
              <span className="text-text-tertiary text-[14px] tabular-nums">{opt.dialCode}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

interface CountryComboboxProps extends BaseProps {
  label: string;
  required?: boolean;
  error?: string;
  autoComplete?: string;
}

export function CountryCombobox({
  label,
  value,
  onChange,
  options,
  placeholder,
  noResultsLabel,
  required,
  error,
  autoComplete = "country-name",
}: CountryComboboxProps) {
  const t = useTranslations("checkout");
  const id = useId();
  const listId = `${id}-list`;
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const resolvedNoResults = noResultsLabel ?? t("countryNoResults");
  const resolvedPlaceholder = placeholder ?? t("countrySearchPlaceholder");

  const selected = useMemo(() => options.find((o) => o.code === value), [options, value]);
  const filtered = useMemo(() => filterCountries(options, query), [options, query]);
  // Clamp during render rather than via setState-in-effect so the active
  // option always points at a valid row even when the filter shrinks.
  const safeActiveIdx = filtered.length === 0 ? 0 : Math.min(activeIdx, filtered.length - 1);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const handleSelect = useCallback(
    (code: CountryCode) => {
      onChange(code);
      setQuery("");
      setOpen(false);
      inputRef.current?.blur();
    },
    [onChange],
  );

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIdx(Math.min(filtered.length - 1, safeActiveIdx + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx(Math.max(0, safeActiveIdx - 1));
    } else if (e.key === "Enter") {
      if (open && filtered[safeActiveIdx]) {
        e.preventDefault();
        handleSelect(filtered[safeActiveIdx].code);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  const inputValue = open ? query : selected?.name ?? "";

  return (
    <div ref={containerRef} className="relative">
      <label htmlFor={id} className="block text-[14px] font-medium text-text-secondary mb-1.5">
        {label} {required && <span className="text-negative">*</span>}
      </label>
      <div
        className={`flex items-center h-12 px-3 bg-background border rounded-xl transition-colors ${
          error ? "border-negative" : open ? "border-foreground" : "border-border"
        }`}
        style={{ transitionDuration: "var(--duration-fast)" }}
      >
        {!open && selected && <FlagImg code={selected.code} size={24} />}
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={open && filtered[safeActiveIdx] ? `${listId}-opt-${filtered[safeActiveIdx].code}` : undefined}
          aria-autocomplete="list"
          autoComplete={autoComplete}
          value={inputValue}
          placeholder={resolvedPlaceholder}
          onFocus={() => {
            setOpen(true);
            setQuery("");
            setActiveIdx(Math.max(0, options.findIndex((o) => o.code === value)));
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
            setActiveIdx(0);
          }}
          onKeyDown={onKeyDown}
          className="flex-1 h-full bg-transparent px-2 text-[16px] text-foreground placeholder:text-text-tertiary outline-none"
        />
        <svg
          className={`w-4 h-4 text-text-tertiary transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-background border border-border rounded-xl shadow-lg overflow-hidden">
          <CountryList
            options={filtered}
            activeIdx={safeActiveIdx}
            onSelect={handleSelect}
            onHover={setActiveIdx}
            listId={listId}
            noResultsLabel={resolvedNoResults}
          />
        </div>
      )}
      {error && <p className="text-[13px] text-negative mt-1">{error}</p>}
    </div>
  );
}

interface CountryDialPickerProps extends BaseProps {
  searchPlaceholder?: string;
}

export function CountryDialPicker({
  value,
  onChange,
  options,
  searchPlaceholder,
  noResultsLabel,
}: CountryDialPickerProps) {
  const t = useTranslations("checkout");
  const resolvedSearchPlaceholder = searchPlaceholder ?? t("countrySearchPlaceholder");
  const resolvedNoResults = noResultsLabel ?? t("countryNoResults");
  const id = useId();
  const listId = `${id}-list`;
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  const selected = useMemo(() => options.find((o) => o.code === value), [options, value]);
  const filtered = useMemo(() => filterCountries(options, query), [options, query]);
  const safeActiveIdx = filtered.length === 0 ? 0 : Math.min(activeIdx, filtered.length - 1);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => searchRef.current?.focus());
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const openPopover = () => {
    setOpen(true);
    setQuery("");
    setActiveIdx(Math.max(0, options.findIndex((o) => o.code === value)));
  };

  const handleSelect = (code: CountryCode) => {
    onChange(code);
    setOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx(Math.min(filtered.length - 1, safeActiveIdx + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx(Math.max(0, safeActiveIdx - 1));
    } else if (e.key === "Enter" && filtered[safeActiveIdx]) {
      e.preventDefault();
      handleSelect(filtered[safeActiveIdx].code);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={selected ? `${selected.name} ${selected.dialCode}` : resolvedSearchPlaceholder}
        onClick={() => (open ? setOpen(false) : openPopover())}
        className="h-full flex items-center gap-2 px-3 border-r border-border bg-transparent text-[16px] text-foreground hover:bg-surface focus:outline-none cursor-pointer"
      >
        {selected && <FlagImg code={selected.code} size={20} />}
        <svg
          className={`w-3 h-3 text-text-tertiary transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-30 left-0 mt-1 w-[320px] bg-background border border-border rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-border">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIdx(0);
              }}
              onKeyDown={onKeyDown}
              placeholder={resolvedSearchPlaceholder}
              aria-controls={listId}
              aria-activedescendant={open && filtered[safeActiveIdx] ? `${listId}-opt-${filtered[safeActiveIdx].code}` : undefined}
              className="w-full h-10 px-3 bg-background border border-border rounded-lg text-[14px] text-foreground placeholder:text-text-tertiary outline-none focus:border-foreground"
            />
          </div>
          <CountryList
            options={filtered}
            activeIdx={safeActiveIdx}
            onSelect={handleSelect}
            onHover={setActiveIdx}
            showDialCode
            listId={listId}
            noResultsLabel={resolvedNoResults}
          />
        </div>
      )}
    </div>
  );
}
